export type HealthStatus = 'healthy' | 'watch' | 'warn' | 'critical';

export type HealthPeriod = {
  at: Date;
  status: HealthStatus;
};

export type HealthTimelineVariant = '30d' | '90d' | '12mo';

export type HealthTimelineProps = {
  periods: HealthPeriod[];
  variant: HealthTimelineVariant;
  barWidth?: number; // px, override the variant default
  state?: 'default' | 'loading' | 'empty';
};

const VARIANT_DEFAULT_BARS: Record<HealthTimelineVariant, number> = {
  '30d': 30,
  '90d': 90,
  '12mo': 52,
};

const STATUS_CLASS: Record<HealthStatus, string> = {
  healthy: 'fill-healthy',
  watch: 'fill-watch',
  warn: 'fill-warn',
  critical: 'fill-critical',
};

function summarize(periods: HealthPeriod[]): string {
  const counts: Record<HealthStatus, number> = {
    healthy: 0,
    watch: 0,
    warn: 0,
    critical: 0,
  };
  for (const p of periods) counts[p.status] += 1;
  return `${counts.healthy} healthy, ${counts.watch} watch, ${counts.warn} warn, ${counts.critical} critical`;
}

const VARIANT_LABEL: Record<HealthTimelineVariant, string> = {
  '30d': 'last 30 days',
  '90d': 'last 90 days',
  '12mo': 'last 12 months',
};

/**
 * Temporal bar strip — one bar per period (day / week / month) colored
 * by per-period health status. Answers "is it getting worse?". Bar
 * radius is intentionally 1px and NOT `r-chip` per components.md.
 */
export function HealthTimeline({
  periods,
  variant,
  barWidth = 4,
  state = 'default',
}: HealthTimelineProps) {
  const expected = VARIANT_DEFAULT_BARS[variant];
  const gap = 2;
  const total = (periods.length || expected) * (barWidth + gap) - gap;
  const height = 18;

  if (state === 'loading') {
    return (
      <div
        role="img"
        aria-label={`Loading health timeline (${VARIANT_LABEL[variant]})`}
        className="flex items-stretch"
        style={{ height, gap: 2 }}
      >
        {Array.from({ length: expected }).map((_, i) => (
          <span
            key={i}
            style={{ width: barWidth }}
            className="animate-pulse bg-gray-100"
          />
        ))}
      </div>
    );
  }

  if (state === 'empty' || periods.length === 0) {
    return (
      <div
        role="img"
        aria-label={`No health data for ${VARIANT_LABEL[variant]}`}
        className="flex items-center"
        style={{ height }}
      >
        <span className="w-full bg-gray-300" style={{ height: 1 }} />
      </div>
    );
  }

  const label = `Health timeline for ${VARIANT_LABEL[variant]}: ${summarize(periods)}`;

  return (
    <svg
      viewBox={`0 0 ${total} ${height}`}
      width={total}
      height={height}
      role="img"
      aria-label={label}
      data-variant={variant}
    >
      {periods.map((p, i) => (
        <rect
          key={`${p.at.toISOString()}-${i}`}
          x={i * (barWidth + gap)}
          y="0"
          width={barWidth}
          height={height}
          rx="1"
          ry="1"
          className={STATUS_CLASS[p.status]}
        />
      ))}
    </svg>
  );
}
